import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* any functionality that external components need, and thus must be exported, goes in this file */
import { useState, useRef, useImperativeHandle, useEffect } from 'react';
import { findOptionByValue } from '@chegg-ui/listbox';
export function useAutocompleteBehavior(ref, disabled, value, options, onInputChange, onAutocompleteSelect, onAutocompleteSubmit, onInputBlur, onInputFocus, onKeydown) {
  var inputRef = useRef(null);
  /* istanbul ignore next */

  useImperativeHandle(ref, function () {
    return inputRef.current;
  });

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isListboxOpen = _useState2[0],
      setIsListboxOpen = _useState2[1]; // unlike the input element itself, our FauxInputCombobox does _not_ receive the css:hover state from the label,
  // so we're forced to track this hover behavior ourselves in order to replicate the behavior of other inputs


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isLabelHovered = _useState4[0],
      setIsLabelHovered = _useState4[1]; // Track focused value from mouse events
  // For mouse events, the component uses css :hover to show focus


  var _useState5 = useState(-1),
      _useState6 = _slicedToArray(_useState5, 2),
      pseudoFocusedListboxIndex = _useState6[0],
      setPseudoFocusedListboxIndex = _useState6[1];

  var _useState7 = useState(-1),
      _useState8 = _slicedToArray(_useState7, 2),
      selectedListboxIndex = _useState8[0],
      setSelectedListboxIndex = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      isComboboxFocused = _useState10[0],
      setIsComboboxFocused = _useState10[1];

  useEffect(function () {
    findOptionByValue(value, options, function (foundIndex) {
      return setSelectedListboxIndex(foundIndex);
    });
  }, [value, options]);

  var open = function open() {
    setIsComboboxFocused(true);
    setIsListboxOpen(true);
  };

  var close = function close() {
    setPseudoFocusedListboxIndex(-1);
    setIsListboxOpen(false);
  };

  var onSelectListboxOption = function onSelectListboxOption(selectedOptionIndex) {
    onAutocompleteSelect(selectedOptionIndex);
    close();
    setIsComboboxFocused(false);
  };
  /*
   * Keyevents when focused
   */


  var onAutocompleteInputKeyDown = function onAutocompleteInputKeyDown(event) {
    if (disabled) return;

    var handleEvent = function handleEvent() {
      event.stopPropagation();
      event.preventDefault();
    };

    switch (event.key) {
      case 'ArrowUp':
      case 'Up':
        {
          if (isListboxOpen && options.length) {
            handleEvent();
            var nextFocus = pseudoFocusedListboxIndex - 1;

            if (nextFocus < 0) {
              nextFocus = options.length - 1;
            }

            setPseudoFocusedListboxIndex(nextFocus);
          }

          break;
        }

      case 'ArrowDown':
      case 'Down':
        {
          if (isListboxOpen && options.length) {
            handleEvent();

            var _nextFocus = pseudoFocusedListboxIndex + 1;

            if (_nextFocus > options.length - 1) {
              _nextFocus = 0;
            }

            setPseudoFocusedListboxIndex(_nextFocus);
          }

          break;
        }

      case 'ArrowLeft':
      case 'Left':
      case 'ArrowRight':
      case 'Right':
        setPseudoFocusedListboxIndex(-1);
        break;

      case 'Enter':
        if (~pseudoFocusedListboxIndex) {
          handleEvent();
          onAutocompleteSelect(pseudoFocusedListboxIndex);
          close();
        } else {
          // we allow the event to propagate & normal browser submit behavior to take over
          onAutocompleteSubmit(event);
          close();
        }

        break;

      case 'Escape':
      case 'Esc':
        handleEvent();
        close();
        break;

      case 'Home':
        {
          handleEvent();
          setPseudoFocusedListboxIndex(-1);
          var inputElement = inputRef.current; // istanbul ignore else

          if (inputElement) {
            if (event.shiftKey) {
              inputElement.setSelectionRange(0, inputElement.selectionStart, 'backward');
            } else {
              inputElement.setSelectionRange(0, 0);
            }
          }

          break;
        }

      case 'End':
        {
          handleEvent();
          setPseudoFocusedListboxIndex(-1);
          var _inputElement = inputRef.current; // istanbul ignore else

          if (_inputElement) {
            if (event.shiftKey) {
              _inputElement.setSelectionRange(_inputElement.selectionStart, _inputElement.value.length, 'forward');
            } else {
              _inputElement.setSelectionRange(_inputElement.value.length, _inputElement.value.length);
            }
          }

          break;
        }

      case 'Tab':
        if (~pseudoFocusedListboxIndex) {
          onAutocompleteSelect(pseudoFocusedListboxIndex);
        } // we don't want to "handle" these events as the rest of the behavior needed is handled by the browser


        break;

      default:
        // we only want to handle "printable" characters and ignore others https://stackoverflow.com/a/49612427
        // we also don't handle these as we want the input itself to follow standard browser behavior
        if (event.key && !event.metaKey) {
          if (!isListboxOpen) {
            if (event.key.length === 1 || event.key === 'Backspace') setIsListboxOpen(true);
          } else {
            onKeydown === null || onKeydown === void 0 ? void 0 : onKeydown(pseudoFocusedListboxIndex, event);
          }
        }

        break;
    }
  };

  return {
    isListboxOpen: isListboxOpen,
    inputRef: inputRef,
    onLabelMouseEnter: function onLabelMouseEnter() {
      return setIsLabelHovered(true);
    },
    onLabelMouseLeave: function onLabelMouseLeave() {
      return setIsLabelHovered(false);
    },
    isLabelHovered: isLabelHovered,
    onAutocompleteInputKeyDown: onAutocompleteInputKeyDown,
    onAutocompleteInputFocus: function onAutocompleteInputFocus(e) {
      open();
      onInputFocus && onInputFocus(e.target.value);
    },
    onAutocompleteInputBlur: function onAutocompleteInputBlur(e) {
      var _inputRef$current;

      /* ignoring next because we will always have input ref element */

      /* istanbul ignore next */
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur(); // This is weird but without this list item was getting selected even after drodown close after interaction with devtools or with some other app

      close();
      setIsComboboxFocused(false);
      onInputBlur && onInputBlur(e.target.value);
    },
    onAutocompleteInputChange: function onAutocompleteInputChange(e) {
      return onInputChange(e.target.value);
    },
    isComboboxFocused: isComboboxFocused,
    onSelectListboxOption: onSelectListboxOption,
    pseudoFocusedListboxIndex: pseudoFocusedListboxIndex,
    selectedListboxIndex: selectedListboxIndex
  };
}